import React, { useState } from 'react';
import Navbar from '../../components/navbar/navbar';
import Footer from '../../components/footer/index';
import { useDarkMode } from '../../components/darkmode/DarkModeContext'; // Import dark mode context
import { Code, Database, Smartphone, Monitor, Cloud, Shield, CheckCircle, Users, Zap, Clock, MessageCircle, ArrowRight, Cpu, Server, Globe } from 'lucide-react';

const CustomSoftwarePage = () => {
  // Use dark mode from context instead of local state
  const { isDarkMode } = useDarkMode();
  const [activeCategory, setActiveCategory] = useState('enterprise');

  // Fungsi untuk mengarahkan ke WhatsApp dengan pesan otomatis
  const contactViaWhatsApp = (serviceName = "Custom Software") => {
    const phoneNumber = "6287778882022"; // Ganti dengan nomor WhatsApp perusahaan
    const message = `Halo, saya tertarik dengan layanan ${serviceName}. Bisakah saya mendapatkan informasi lebih lanjut?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  const categories = [
    { id: 'enterprise', name: 'Enterprise Solution', icon: <Database className="w-6 h-6" /> },
    { id: 'business', name: 'Business Application', icon: <Monitor className="w-6 h-6" /> },
    { id: 'mobile', name: 'Mobile App', icon: <Smartphone className="w-6 h-6" /> },
    { id: 'microsoft', name: 'Microsoft License', icon: <Cloud className="w-6 h-6" /> },
    { id: 'custom', name: 'Custom Development', icon: <Code className="w-6 h-6" /> }
  ];

  const softwareSolutions = {
    enterprise: [
      {
        icon: <Database className="w-10 h-10" />,
        title: "SIMRS (Sistem Informasi Manajemen Rumah Sakit)",
        description: "Sistem terintegrasi untuk manajemen operasional rumah sakit yang komprehensif",
        features: ["Manajemen pasien & rekam medis", "Farmasi & inventory", "Billing & keuangan", "Laporan & analytics"],
        image: "/image/layanan/simrs.png"
      },
      {
        icon: <Cpu className="w-10 h-10" />,
        title: "ERP (Enterprise Resource Planning)",
        description: "Sistem terpadu untuk mengelola semua proses bisnis dalam satu platform",
        features: ["Supply chain management", "Manufacturing & production", "Human resources", "Financial management"],
        image: "/image/layanan/erp.png"
      },
      {
        icon: <Users className="w-10 h-10" />,
        title: "CRM (Customer Relationship Management)",
        description: "Sistem untuk mengelola hubungan dengan pelanggan dan meningkatkan penjualan",
        features: ["Sales automation", "Customer service", "Marketing automation", "Analytics & reporting"],
        image: "/image/layanan/crm.png"
      },
      {
        icon: <Server className="w-10 h-10" />,
        title: "HRIS (Human Resource Information System)",
        description: "Sistem untuk mengelola sumber daya manusia secara efisien dan terintegrasi",
        features: ["Payroll & benefits", "Recruitment & onboarding", "Performance management", "Training & development"],
        image: "/image/layanan/hris.png"
      }
    ],
    business: [
      {
        icon: <Monitor className="w-10 h-10" />,
        title: "Aplikasi Kasir & Point of Sale",
        description: "Sistem kasir modern untuk retail, restaurant, dan berbagai jenis bisnis",
        features: ["Multi-outlet management", "Inventory tracking", "Sales reporting", "Integration with payment systems"],
        image: "/image/layanan/pos.png"
      },
      {
        icon: <Globe className="w-10 h-10" />,
        title: "E-Commerce Platform",
        description: "Platform penjualan online yang lengkap dan dapat disesuaikan",
        features: ["Product catalog management", "Shopping cart & checkout", "Payment gateway integration", "Order management"],
        image: "/image/layanan/ecommerce.png"
      },
      {
        icon: <Shield className="w-10 h-10" />,
        title: "Sistem Akuntansi & Keuangan",
        description: "Software akuntansi komprehensif untuk manajemen keuangan bisnis",
        features: ["General ledger", "Accounts payable/receivable", "Tax management", "Financial reporting"],
        image: "/image/layanan/accounting.png"
      },
      {
        icon: <Cloud className="w-10 h-10" />,
        title: "E-Arsip & Document Management",
        description: "Sistem manajemen dokumen digital untuk arsip dan penyimpanan yang aman",
        features: ["Document scanning & indexing", "Version control", "Access control", "Search & retrieval"],
        image: "/image/layanan/document-management.png"
      }
    ],
    mobile: [
      {
        icon: <Smartphone className="w-10 h-10" />,
        title: "Aplikasi Klinik & Apotek",
        description: "Mobile solution untuk manajemen praktik medis dan farmasi",
        features: ["Patient management", "Appointment scheduling", "Prescription management", "Inventory tracking"],
        image: "/image/layanan/clinic-app.png"
      },
      {
        icon: <Zap className="w-10 h-10" />,
        title: "Delivery & Logistics App",
        description: "Aplikasi untuk manajemen pengiriman dan logistik yang efisien",
        features: ["Order tracking", "Driver management", "Route optimization", "Real-time updates"],
        image: "/image/layanan/delivery-app.png"
      },
      {
        icon: <Users className="w-10 h-10" />,
        title: "Sistem Informasi Sekolah/Kampus",
        description: "Platform lengkap untuk manajemen institusi pendidikan",
        features: ["Student information system", "Academic scheduling", "Grade management", "Parent portal"],
        image: "/image/layanan/school-management.png"
      },
      {
        icon: <Code className="w-10 h-10" />,
        title: "Custom Mobile Applications",
        description: "Pengembangan aplikasi mobile khusus untuk iOS dan Android",
        features: ["Native iOS/Android development", "Cross-platform solutions", "UI/UX design", "App store deployment"],
        image: "/image/layanan/custom-mobile.png"
      }
    ],
    microsoft: [
      {
        icon: <Cloud className="w-10 h-10" />,
        title: "Microsoft Windows License",
        description: "Lisensi resmi Microsoft Windows untuk semua versi",
        features: ["Windows 10/11 Professional", "Volume licensing", "Enterprise edition", "Long-term support"],
        image: "/image/layanan/windows-license.png"
      },
      {
        icon: <Monitor className="w-10 h-10" />,
        title: "Microsoft Office 365",
        description: "Suite produktivitas cloud dengan fitur kolaborasi terbaik",
        features: ["Word, Excel, PowerPoint", "OneDrive cloud storage", "Microsoft Teams", "Business/Enterprise plans"],
        image: "/image/layanan/office365.png"
      },
      {
        icon: <Server className="w-10 h-10" />,
        title: "Microsoft Server Solutions",
        description: "Lisensi server Microsoft untuk infrastruktur IT enterprise",
        features: ["Windows Server", "SQL Server", "Exchange Server", "SharePoint Server"],
        image: "/image/layanan/server-license.png"
      },
      {
        icon: <Shield className="w-10 h-10" />,
        title: "Microsoft Security & Compliance",
        description: "Solusi keamanan dan compliance untuk perlindungan data",
        features: ["Microsoft Defender", "Azure Information Protection", "Compliance Manager", "Advanced Threat Protection"],
        image: "/image/layanan/security-license.png"
      }
    ],
    custom: [
      {
        icon: <Code className="w-10 h-10" />,
        title: "Web Application Development",
        description: "Pengembangan aplikasi web custom dengan teknologi terkini",
        features: ["Responsive design", "Modern frameworks (React, Vue, Angular)", "RESTful APIs", "Cloud deployment"],
        image: "/image/layanan/web-development.png"
      },
      {
        icon: <Monitor className="w-10 h-10" />,
        title: "Desktop Application Development",
        description: "Aplikasi desktop untuk Windows, macOS, dan Linux",
        features: ["Native performance", "Database integration", "Offline capability", "Cross-platform compatibility"],
        image: "/image/layanan/desktop-app.png"
      },
      {
        icon: <Database className="w-10 h-10" />,
        title: "Database & System Integration",
        description: "Integrasi sistem dan pengembangan database custom",
        features: ["Database design & optimization", "API development", "Legacy system integration", "Data migration"],
        image: "/image/layanan/database-integration.png"
      },
      {
        icon: <Zap className="w-10 h-10" />,
        title: "Payment Gateway Integration",
        description: "Integrasi sistem pembayaran untuk e-commerce dan bisnis",
        features: ["Multiple payment methods", "Security compliance (PCI DSS)", "Recurring billing", "Multi-currency support"],
        image: "/image/layanan/payment-gateway.png"
      }
    ]
  };

  const benefits = [
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Custom Sesuai Kebutuhan",
      description: "Dikembangkan khusus sesuai kebutuhan dan proses bisnis Anda"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Keamanan Terjamin",
      description: "Dibangun dengan standar keamanan tinggi untuk melindungi data"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Teknologi Terkini",
      description: "Menggunakan framework dan teknologi modern yang terupdate"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Tim Expert Berpengalaman",
      description: "Dikerjakan oleh developer dan analis yang profesional"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Support & Maintenance",
      description: "Layanan dukungan dan pemeliharaan pasca implementasi"
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: "Scalable Solution",
      description: "Dapat dikembangkan seiring dengan pertumbuhan bisnis Anda"
    }
  ];

  const developmentProcess = [
    {
      step: "1",
      title: "Konsultasi & Analisis",
      description: "Understanding kebutuhan bisnis dan analisis requirement"
    },
    {
      step: "2",
      title: "Perencanaan & Desain",
      description: "Membuat blueprint sistem dan user experience design"
    },
    {
      step: "3",
      title: "Development",
      description: "Proses coding dan pengembangan aplikasi"
    },
    {
      step: "4",
      title: "Testing & Quality Assurance",
      description: "Pengujian menyeluruh untuk memastikan kualitas"
    },
    {
      step: "5",
      title: "Deployment & Implementasi",
      description: "Instalasi dan implementasi sistem di lingkungan production"
    },
    {
      step: "6",
      title: "Training & Support",
      description: "Pelatihan pengguna dan dukungan berkelanjutan"
    }
  ];

  const technologies = [
    { name: "React", logo: "/image/react.png", type: "Frontend" },
    { name: "Vue.js", logo: "/image/vue.png", type: "Frontend" },
    { name: "Angular", logo: "/image/angular.png", type: "Frontend" },
    { name: "Node.js", logo: "/image/nodejs.png", type: "Backend" },
    { name: "Laravel", logo: "/image/laravel.png", type: "Backend" },
    { name: "Django", logo: "/image/django.png", type: "Backend" },
    { name: "Flutter", logo: "/image/flutter.png", type: "Mobile" },
    { name: "React Native", logo: "/image/react-native.png", type: "Mobile" },
    { name: "MySQL", logo: "/image/mysql.png", type: "Database" },
    { name: "PostgreSQL", logo: "/image/postgresql.png", type: "Database" },
    { name: "MongoDB", logo: "/image/mongodb.png", type: "Database" },
    { name: "AWS", logo: "/image/aws.png", type: "Cloud" }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-[#dfe4e0ff]'
    }`}>
      
      <Navbar />
      
      {/* Hero Section */}
      <section
        className={`py-20 px-4 relative overflow-hidden ${
          isDarkMode ? 'bg-gray-900' : ''
        }`}
        style={{
          backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
          backgroundImage: isDarkMode
            ? "url('/image/software-development-dark.jpg')"
            : "url('/image/software-development.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-gradient-to-r from-gray-900/80 to-gray-800/80' 
            : 'bg-gradient-to-r from-[#1E9B4C]/20 to-green-700/20'
        }`}></div>

        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className={`inline-flex items-center gap-2 ${
              isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-[#1E9B4C]'
            } px-4 py-2 rounded-full text-sm font-medium mb-6`}>
              <Code className="w-4 h-4" />
              Custom Software Development
            </div>

            <h1
              className={`text-4xl md:text-6xl font-bold mb-6 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Layanan Custom
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#1E9B4C] to-green-700">
                Software & Aplikasi
              </span>
            </h1>

            <p
              className={`text-xl max-w-3xl mx-auto mb-8 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Kami menawarkan layanan pengembangan perangkat lunak kustom yang dirancang khusus 
              untuk memenuhi kebutuhan spesifik bisnis atau organisasi Anda, termasuk SIMRS, ERP, CRM, 
              dan berbagai aplikasi khusus lainnya.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => contactViaWhatsApp("Custom Software")}
                className="bg-[#1E9B4C] hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Konsultasi Gratis
              </button>
            </div>
          </div>
        </div>
      </section>

       <div className="absolute inset-0">
        <img
          src="https://telnet.net.id/assets/bg.png"
          alt="Background pattern"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-transparent dark:via-black/40"></div>
      </div>

      {/* Categories Section */}
      <section className={`py-16 px-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Kategori Layanan
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Pilih kategori layanan pengembangan software yang Anda butuhkan
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          {/* Desktop Categories */}
          <div className="hidden md:grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`p-6 rounded-xl transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-[#1E9B4C] text-white shadow-lg transform scale-105'
                    : isDarkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-green-50 hover:text-[#1E9B4C]'
                }`}
              >
                <div className="flex flex-col items-center">
                  <div className="mb-3">
                    {category.icon}
                  </div>
                  <span className="font-medium text-sm text-center">{category.name}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Mobile Categories - Horizontal Scrollable */}
          <div className="md:hidden">
            <div className="flex space-x-4 overflow-x-auto pb-4 scrollbar-hide" style={{ scrollSnapType: 'x mandatory' }}>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex-shrink-0 p-4 rounded-xl transition-all duration-300 min-w-[140px] ${
                    activeCategory === category.id
                      ? 'bg-[#1E9B4C] text-white shadow-lg'
                      : isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-green-50'
                  }`}
                  style={{ scrollSnapAlign: 'start' }}
                >
                  <div className="flex flex-col items-center">
                    <div className="mb-2">
                      {category.icon}
                    </div>
                    <span className="font-medium text-sm text-center">{category.name}</span>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Mobile scroll indicator */}
            <div className="flex justify-center mt-2">
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Geser untuk melihat kategori lainnya →
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Software Solutions Section */}
      <section className={`py-16 px-4 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {categories.find(cat => cat.id === activeCategory)?.name}
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Solusi software terbaik untuk {categories.find(cat => cat.id === activeCategory)?.name.toLowerCase()}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {softwareSolutions[activeCategory].map((solution, index) => (
              <div
                key={index}
                className={`rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700' 
                    : 'bg-white border border-gray-100'
                }`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={solution.image}
                    alt={solution.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${
                    isDarkMode ? 'from-gray-900/70 to-transparent' : 'from-black/40 to-transparent'
                  }`}></div>
                  <div className="absolute bottom-4 left-4">
                    <div className={`p-3 rounded-lg ${
                      isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'
                    } backdrop-blur-sm inline-flex`}>
                      {solution.icon}
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {solution.title}
                  </h3>
                  <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {solution.description}
                  </p>
                  
                  <div className="space-y-2 mb-6">
                    {solution.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0" />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => contactViaWhatsApp(solution.title)}
                    className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                      isDarkMode
                        ? 'bg-green-700 hover:bg-green-600 text-white'
                        : 'bg-[#1E9B4C] hover:bg-green-700 text-white'
                    }`}
                  >
                    Konsultasi Sekarang
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={`py-16 px-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Keunggulan Layanan Kami
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Mengapa harus memilih layanan custom software development dari kami
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl transition-all duration-300 hover:scale-105 ${
                  isDarkMode
                    ? 'bg-gray-700 border border-gray-600 hover:border-[#1E9B4C]'
                    : 'bg-gray-50 border border-gray-100 hover:border-[#1E9B4C]'
                }`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                  isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-[#1E9B4C]'
                }`}>
                  {benefit.icon}
                </div>
                <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {benefit.title}
                </h3>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Development Process */}
      <section className={`py-16 px-4 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Proses Pengembangan
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Langkah-langkah profesional dalam mengembangkan software custom untuk Anda
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {developmentProcess.map((step, index) => (
              <div
                key={index}
                className={`relative p-6 rounded-xl border-2 transition-all duration-300 ${
                  isDarkMode
                    ? 'bg-gray-800 border-gray-700 hover:border-[#1E9B4C]'
                    : 'bg-white border-gray-200 hover:border-[#1E9B4C] shadow-md'
                }`}
              >
                <div className={`absolute -top-4 -left-4 w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  isDarkMode ? 'bg-[#1E9B4C] text-white' : 'bg-green-100 text-[#1E9B4C]'
                }`}>
                  {step.step}
                </div>
                <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {step.title}
                </h3>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className={`py-16 px-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Teknologi yang Kami Gunakan
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Menggunakan teknologi modern dan terdepan untuk pengembangan software
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {technologies.map((tech, index) => (
              <div                key={index}
                className={`flex flex-col items-center p-4 rounded-lg transition-all duration-300 ${
                  isDarkMode
                    ? 'bg-gray-700 border border-gray-600 hover:border-[#1E9B4C]'
                    : 'bg-gray-50 border border-gray-200 hover:border-[#1E9B4C]'
                }`}
              >
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="w-12 h-12 object-contain mb-2"
                />
                <span className={`text-sm font-medium ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {tech.name}
                </span>
                <span className={`text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {tech.type}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
  className={`py-16 px-4 transition-colors duration-300 ${
    isDarkMode ? 'bg-gray-800' : 'bg-[#1E9B4C]'
  }`}
>
  <div className="container mx-auto text-center">
    <h2
      className={`text-3xl font-bold mb-4 ${
        isDarkMode ? 'text-white' : 'text-white'
      }`}
    >
      Siap Mengembangkan Software Custom untuk Bisnis Anda?
    </h2>
    <p
      className={`text-xl mb-8 max-w-2xl mx-auto ${
        isDarkMode ? 'text-green-200' : 'text-green-100'
      }`}
    >
      Hubungi kami sekarang untuk konsultasi gratis dan dapatkan solusi software{' '}
      yang tepat untuk kebutuhan bisnis Anda.
    </p>
    <div className="flex flex-col sm:flex-row gap-4 justify-center">
      <button
        onClick={() => contactViaWhatsApp('Custom Software Development')}
        className={`font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center ${
          isDarkMode
            ? 'bg-white text-[#1E9B4C] hover:bg-gray-100'
            : 'bg-white text-[#1E9B4C] hover:bg-gray-100'
        }`}
      >
        <Code className="w-5 h-5" />
        Konsultasi Gratis
      </button>
      <button
        onClick={() => contactViaWhatsApp('Enterprise Solution')}
        className={`border-2 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center ${
          isDarkMode
            ? 'border-white text-white hover:bg-white hover:text-[#1E9B4C]'
            : 'border-white text-white hover:bg-white hover:text-[#1E9B4C]'
        }`}
      >
        <ArrowRight className="w-5 h-5" />
        Enterprise Solution
      </button>
    </div>
  </div>
</section>


      <Footer />
    </div>
  );
};

export default CustomSoftwarePage;